/**
 * PDKS QR Giriş/Çıkış — personel terminaldeki dinamik QR'ı telefonuyla okutunca açılır.
 * Kimlik aktif oturumdan gelir; QR yalnız kısa süreli terminal token'ı taşır.
 */
import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, LogIn, LogOut, CheckCircle2, XCircle } from "lucide-react";

export const POST_LOGIN_KEY = "acuto:postLogin";
export const DEVICE_KEY = "acuto:pdksDevice";

// PDKS mobil cihaz token'ı varsa ERP oturumu yerine o kullanılır
const deviceHeaders = () => {
  const d = localStorage.getItem(DEVICE_KEY);
  return d ? { headers: { "X-PDKS-Device": d } } : {};
};

const fmtTime = (iso) => new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

export default function PdksQrStamp() {
  const { user } = useAuth();
  const [hasDevice] = useState(() => !!localStorage.getItem(DEVICE_KEY));
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const onErr = (e) => {
    if (hasDevice && e?.response?.status === 401) localStorage.removeItem(DEVICE_KEY);
    setError(formatApiError(e));
  };

  useEffect(() => {
    if ((!hasDevice && !user) || !t) return;
    api.post("/pdks/qr/check", { t }, deviceHeaders())
      .then(({ data }) => setInfo(data))
      .catch(onErr);
  }, [user, t, hasDevice]); // eslint-disable-line

  if (!hasDevice && user === null) return <Shell><Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" /></Shell>;
  if (!hasDevice && user === false) {
    if (!params.get("erp")) {
      return (
        <Shell>
          <Msg icon={<XCircle className="w-16 h-16 text-amber-400 mx-auto" />} testId="qr-no-device"
            text="Bu telefon PDKS için tanımlı değil. Yöneticinizden aktivasyon bağlantısı isteyin." />
          <a href={`/pdks/qr?t=${encodeURIComponent(t)}&erp=1`} className="block mt-8 text-xs text-slate-500 underline" data-testid="qr-erp-login-link">
            ACUTO 360 kullanıcı hesabıyla giriş yap
          </a>
        </Shell>
      );
    }
    sessionStorage.setItem(POST_LOGIN_KEY, `/pdks/qr?t=${encodeURIComponent(t)}`);
    return <Navigate to="/login" replace />;
  }

  const stamp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pdks/qr/stamp", { t }, deviceHeaders());
      if (data?.ok === false) setError(data.message || "İşlem yapılamadı");
      else setDone(data.event);
    } catch (e) {
      onErr(e);
    } finally { setBusy(false); }
  };

  const isIn = info?.next_type === "check_in";
  return (
    <Shell>
      {!t ? (
        <Msg icon={<XCircle className="w-16 h-16 text-rose-500 mx-auto" />} text="Geçersiz bağlantı. Terminaldeki QR kodu okutun." testId="qr-error" />
      ) : done ? (
        <Msg icon={<CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" />} testId="qr-done"
          text={`${done.event_type === "check_in" ? "Girişiniz" : "Çıkışınız"} kaydedildi — ${fmtTime(done.event_datetime)}`} />
      ) : null}
      {(done || error) && params.get("app") ? (
        <a href="/pdks/app" className="block mt-8 text-sm text-blue-400 underline" data-testid="qr-back-to-app">QR Okutma Ekranına Dön</a>
      ) : null}
      {!t || done ? null : error ? (
        <Msg icon={<XCircle className="w-16 h-16 text-rose-500 mx-auto" />} text={error} testId="qr-error" />
      ) : !info ? (
        <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
      ) : (
        <div className="space-y-6">
          <div>
            <div className="text-2xl font-semibold" data-testid="qr-employee-name">{info.employee?.name}</div>
            {info.employee?.department && <div className="text-slate-400 text-sm">{info.employee.department}</div>}
            {info.last_event && (
              <div className="text-slate-500 text-xs mt-2">
                Son işlem: {info.last_event.event_type === "check_in" ? "Giriş" : "Çıkış"} · {fmtTime(info.last_event.event_datetime)}
              </div>
            )}
          </div>
          <Button onClick={stamp} disabled={busy} data-testid="qr-stamp-btn"
            className={`w-full h-20 text-2xl font-bold rounded-md ${isIn ? "bg-emerald-600 hover:bg-emerald-500" : "bg-rose-600 hover:bg-rose-500"}`}>
            {busy ? <Loader2 className="w-7 h-7 animate-spin" />
              : isIn ? <><LogIn className="w-7 h-7 mr-3" /> GİRİŞ YAP</> : <><LogOut className="w-7 h-7 mr-3" /> ÇIKIŞ YAP</>}
          </Button>
        </div>
      )}
    </Shell>
  );
}

// Eski aktivasyon linkleri (?c=) yeni kurulum ekranına yönlenir — açılış token TÜKETMEZ
export function PdksActivate() {
  const [params] = useSearchParams();
  const c = params.get("c") || "";
  return <Navigate to={`/pdks/kurulum#k=${encodeURIComponent(c)}`} replace />;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 text-center" data-testid="pdks-qr-page">
      <div className="text-xs uppercase tracking-widest text-blue-500 font-mono mb-1">ACUTO 360 · PDKS</div>
      <div className="text-lg font-semibold mb-10">Personel Giriş / Çıkış</div>
      <div className="w-full max-w-sm">{children}</div>
    </div>
  );
}

function Msg({ icon, text, testId }) {
  return (
    <div className="space-y-4" data-testid={testId}>
      {icon}
      <div className="text-xl font-semibold">{text}</div>
    </div>
  );
}
